import React, { useEffect, useState } from 'react';
import { inputs } from '@ecosystem/newron-design-system';
import { Market, TimeFrame, TimeFrameOption } from '../types';
import { getWeekEnd, getWeekStart } from './weeksSelector.utils';
import { isCustomWeekly } from './periodSelector.utils';
import { Periodicity } from '@ecosystem/newron-core/types';
import { ApplyPeriodButton } from './applyPeriod.button';
import { WARNING_MESSAGE_ON_APPLY } from './constants';
import { useMobile } from '../../providers/mobile/mobile.provider';

const { WeekPicker, WeekFormatType } = inputs;

const isISOWeekNumber = false;

type Props = {
  weeklyOptions: TimeFrameOption[];
  markets: Market[];
  currentValue: TimeFrame;
  onSelect: (value: TimeFrameOption, removeAffectedMarkets: boolean) => void;
};

export function WeeksSelector({ weeklyOptions = [], markets = [], currentValue, onSelect }: Props) {
  const { isMobile } = useMobile();
  const [weekFrom, setWeekFrom] = useState<{ timeFrame: TimeFrameOption; startDate: Date }>(null);
  const [weekTo, setWeekTo] = useState<{ timeFrame: TimeFrameOption; startDate: Date }>(null);
  const [isWrongDateRange, setIsWrongDateRange] = useState(false);

  // chack markets
  const notAvailableMarkets = markets.filter(m => m.periods.every(p => p.periodicity !== Periodicity.Weekly));
  const notPublishedMarkets = markets.filter(m =>
    m.periods.find(p => p.periodicity === Periodicity.Weekly && p.sequence < weekTo?.timeFrame.endSequence)
  );

  //  sort and map options for fast direct access
  const optionsMap = new Map(weeklyOptions.map(opt => [opt.label, opt]));
  const optionsSorted = [...weeklyOptions].sort((a, b) => a.endSequence - b.endSequence);

  // get min and max possible options
  const minDate = getWeekStart(optionsSorted[0], isISOWeekNumber);
  const maxDate = getWeekEnd(optionsSorted[optionsSorted.length - 1], isISOWeekNumber);

  useEffect(() => {
    // prefill values - either current value or the latest period
    if (!!weekFrom && !!weekTo) {
      return;
    }
    if (!optionsSorted || optionsSorted.length === 0) {
      return;
    }

    const setCurrentDates = () => {
      const startPeriod = optionsSorted.find(opt => opt.endSequence === currentValue.startSequence);
      const endPeriod = optionsSorted.find(opt => opt.endSequence === currentValue.endSequence);
      setWeekFrom({ timeFrame: startPeriod, startDate: getWeekStart(startPeriod, isISOWeekNumber) });
      setWeekTo({ timeFrame: endPeriod, startDate: getWeekStart(endPeriod, isISOWeekNumber) });
    };

    const setLatestPeriod = () => {
      const defaultWeekFrom = optionsSorted[optionsSorted.length - 2];
      const defaultWeekTo = optionsSorted[optionsSorted.length - 1];
      setWeekFrom({ timeFrame: defaultWeekFrom, startDate: getWeekStart(defaultWeekFrom, isISOWeekNumber) });
      setWeekTo({ timeFrame: defaultWeekTo, startDate: getWeekStart(defaultWeekTo, isISOWeekNumber) });
    };

    if (currentValue && isCustomWeekly(currentValue)) {
      setCurrentDates();
    } else {
      setLatestPeriod();
    }
  }, [optionsSorted, currentValue, weekFrom, weekTo]);

  useEffect(() => {
    // check if dates are valid
    if (!!weekFrom?.startDate && !!weekTo?.startDate) {
      setIsWrongDateRange(weekFrom?.startDate > weekTo?.startDate);
    }
  }, [weekFrom?.startDate, weekTo?.startDate]);

  const handleApply = () => {
    if (weekFrom && weekTo) {
      const customTimeFramePeriod = {
        ...weekFrom?.timeFrame,
        value: `Weekly:${weekFrom.timeFrame.endSequence}-${weekTo.timeFrame.endSequence}:Custom`,
      };
      onSelect(customTimeFramePeriod, false);
    }
  };

  return (
    <div
      data-testid="weekly-selector-container"
      className={`flex flex-column ${isMobile ? 'min-h-0 h-full justify-start' : 'h-auto justify-end'}`}
    >
      <div
        data-testid="weekly-selector"
        className={'p-4 pt-0'}
        style={{
          marginBottom: isMobile ? '0' : 'auto',
        }}
      >
        <WeekPicker
          label={'From'}
          defaultDate={weekFrom?.startDate}
          dateFormat={WeekFormatType['Ww yyyy']}
          isISOWeekNumber={isISOWeekNumber}
          minDate={minDate}
          maxDate={maxDate}
          placeholder="Select week"
          onChange={val => {
            if (val !== weekFrom?.timeFrame?.label && optionsMap.has(val)) {
              const selected = optionsMap.get(val);
              const startDate = getWeekStart(selected, isISOWeekNumber);
              setWeekFrom({ timeFrame: selected, startDate });
            }
          }}
        />
        <WeekPicker
          label={'To'}
          defaultDate={weekTo?.startDate}
          dateFormat={WeekFormatType['Ww yyyy']}
          isISOWeekNumber={isISOWeekNumber}
          minDate={minDate}
          maxDate={maxDate}
          placeholder="Select week"
          onChange={val => {
            if (val !== weekTo?.timeFrame?.label && optionsMap.has(val)) {
              const selected = optionsMap.get(val);
              const startDate = getWeekStart(selected, isISOWeekNumber);
              setWeekTo({ timeFrame: selected, startDate });
            }
          }}
        />
        {isWrongDateRange && <div className={'error-message'}>{WARNING_MESSAGE_ON_APPLY}</div>}
      </div>
      <hr />
      <ApplyPeriodButton
        notPublishedMarkets={notPublishedMarkets}
        notAvailableMarkets={notAvailableMarkets}
        onApply={handleApply}
        disabled={!weekFrom || !weekTo || isWrongDateRange}
      />
    </div>
  );
}
